import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import { AuthApi } from "@/api/auth.api";
import { queryClient } from "@/query-client";
import { usePlayerStore } from "@/stores/player.store";
import type { Credential, User } from "@/types";

import { migrateSessionStorage } from "./session.store-migration";

type UnauthenticatedState = {
  status: "unauthenticated";
};

type AuthenticatedState = {
  status: "authenticated";
  authToken: string;
  user: User;
};

type SessionState = UnauthenticatedState | AuthenticatedState;

export const useSessionStore = defineStore(
  "session",
  () => {
    const session = ref<SessionState>({
      status: "unauthenticated",
    });
    const authToken = computed(() =>
      session.value.status === "authenticated"
        ? session.value.authToken
        : undefined,
    );
    const user = computed(() =>
      session.value.status === "authenticated" ? session.value.user : undefined,
    );
    let sessionAbortController = new AbortController();
    let loginAbortController: AbortController | undefined;

    function replaceSession(authenticatedState: AuthenticatedState) {
      endSessionWork();
      usePlayerStore().reset();
      queryClient.getQueryCache().clear();
      session.value = authenticatedState;
    }

    function endSessionWork() {
      sessionAbortController.abort();
      sessionAbortController = new AbortController();
    }

    async function login(credential: Credential) {
      loginAbortController?.abort();

      const attemptController = new AbortController();

      loginAbortController = attemptController;

      try {
        const loginResult = await AuthApi.login(credential, {
          signal: attemptController.signal,
        });

        attemptController.signal.throwIfAborted();

        replaceSession({
          status: "authenticated",
          authToken: loginResult.authToken,
          user: loginResult.user,
        });
      } catch (error) {
        attemptController.signal.throwIfAborted();
        throw error;
      } finally {
        if (loginAbortController === attemptController) {
          loginAbortController = undefined;
        }
      }
    }

    function logout() {
      loginAbortController?.abort();
      loginAbortController = undefined;
      endSessionWork();
      usePlayerStore().reset();
      session.value = { status: "unauthenticated" };

      queryClient.clear();
    }

    function currentSessionSignal() {
      return sessionAbortController.signal;
    }

    return {
      session,
      authToken,
      user,
      login,
      logout,
      currentSessionSignal,
    };
  },
  {
    persist: {
      key: "session",
      pick: ["session"],
      beforeHydrate: () => migrateSessionStorage(localStorage),
    },
  },
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSessionStore, import.meta.hot));
}
